import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { exportMuestrasToPdf } from "./muestrasPdfExport";
import MuestrasSavePdfToCompassDialog from "./MuestrasSavePdfToCompassDialog";
import { useTranslation } from "react-i18next";

function guessCompassHints(muestras, selectedIds) {
  const idSet = new Set((selectedIds || []).map((id) => Number(id)));
  const selected = (muestras || []).filter((m) =>
    idSet.has(Number(m.id ?? m.ID_MUESTRA)),
  );
  const seasons = selected
    .map((m) => String(m.TEMPORADA || m.Temporada || "").trim())
    .filter(Boolean);
  const seasonCounts = {};
  for (const s of seasons) seasonCounts[s] = (seasonCounts[s] || 0) + 1;
  const suggestedTemporada =
    Object.entries(seasonCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
  const customerIds = selected
    .map((m) => Number(m.ID_CLIENTE || m.CustomerId || m.customerId))
    .filter((n) => Number.isFinite(n) && n > 0);
  const cidCounts = {};
  for (const id of customerIds) cidCounts[id] = (cidCounts[id] || 0) + 1;
  const suggestedCustomerId =
    Number(Object.entries(cidCounts).sort((a, b) => b[1] - a[1])[0]?.[0]) ||
    null;
  return { suggestedTemporada, suggestedCustomerId };
}

const MuestrasExportPdfDialog = ({
  muestras,
  selectedIds = [],
  buttonProps,
  iconOnly = false,
}) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [saveOpen, setSaveOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [savedOk, setSavedOk] = useState(false);

  const hints = useMemo(
    () => guessCompassHints(muestras, selectedIds),
    [muestras, selectedIds],
  );

  const handleExport = async () => {
    if (!selectedIds.length || exporting) return;

    setConfirmOpen(false);
    setExporting(true);
    setExportError("");
    setProgress({ current: 0, total: 0 });
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `Muestras_SDS_${stamp}.pdf`;
      const blob = await exportMuestrasToPdf({
        muestras: muestras || [],
        selectedIds,
        filename,
        onProgress: setProgress,
        download: false,
      });
      setPdfBlob(blob);
      setPdfFilename(filename);
      setSaveOpen(true);
    } catch (err) {
      console.error("Error exportando PDF muestras:", err);
      setExportError(err?.message || t("Samples.pdfExportError"));
    } finally {
      setExporting(false);
    }
  };

  const progressLabel =
    progress.total > 0
      ? t("Samples.pdfGeneratingProgress", {
          current: progress.current,
          total: progress.total,
        })
      : t("Samples.pdfGenerating");

  const pdfTooltip = selectedIds.length
    ? t("Samples.pdfSelectedTooltip")
    : t("Samples.pdfSelectFirstTooltip");

  return (
    <>
      <Tooltip title={pdfTooltip}>
        <span>
          {iconOnly ? (
            <Badge
              color="primary"
              badgeContent={selectedIds.length}
              invisible={selectedIds.length === 0}
              overlap="circular"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.6rem",
                  minWidth: 16,
                  height: 16,
                },
              }}
            >
              <IconButton
                size="small"
                color="primary"
                onClick={() => setConfirmOpen(true)}
                disabled={exporting || selectedIds.length === 0}
                aria-label={
                  exporting ? progressLabel : `PDF (${selectedIds.length})`
                }
                {...buttonProps}
              >
                {exporting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PictureAsPdfOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Badge>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setConfirmOpen(true)}
              disabled={exporting || selectedIds.length === 0}
              startIcon={
                exporting ? (
                  <CircularProgress size={17} color="inherit" />
                ) : (
                  <PictureAsPdfOutlinedIcon />
                )
              }
              {...buttonProps}
            >
              {exporting ? progressLabel : `PDF (${selectedIds.length})`}
            </Button>
          )}
        </span>
      </Tooltip>

      <Dialog
        open={confirmOpen}
        onClose={() => !exporting && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("Samples.pdfConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Samples.pdfConfirmBody", { count: selectedIds.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={exporting}>
            {t("Samples.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={exporting || selectedIds.length === 0}
            startIcon={<PictureAsPdfOutlinedIcon />}
          >
            {t("Samples.pdfConfirmButton")}
          </Button>
        </DialogActions>
      </Dialog>

      <MuestrasSavePdfToCompassDialog
        open={saveOpen}
        blob={pdfBlob}
        filename={pdfFilename}
        suggestedTemporada={hints.suggestedTemporada}
        suggestedCustomerId={hints.suggestedCustomerId}
        onClose={() => {
          setSaveOpen(false);
          setPdfBlob(null);
        }}
        onSaved={() => setSavedOk(true)}
      />

      <Snackbar
        open={Boolean(exportError)}
        autoHideDuration={6000}
        onClose={() => setExportError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setExportError("")}
        >
          {exportError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={savedOk}
        autoHideDuration={4000}
        onClose={() => setSavedOk(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSavedOk(false)}
        >
          {t("Samples.savePdfSuccess", {
            defaultValue: "PDF guardado en PRESENTACIONES PDF de Compás.",
          })}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MuestrasExportPdfDialog;
